/**
 * useInstallPrompt — captura el evento `beforeinstallprompt` del browser
 * (Chrome/Edge/Opera) y expone `prompt()` para disparar el diálogo nativo
 * "Instalar app" cuando el usuario lo decide.
 *
 * Notas:
 *   - Safari iOS NO emite este evento (Apple no lo soporta). Para iOS
 *     hay que mostrar instrucciones manuales (compartir → Agregar a
 *     pantalla de inicio).
 *   - Firefox Android tampoco lo soporta a la fecha (2026).
 *   - El evento se emite UNA vez por sesión y solo si el browser
 *     considera que la PWA cumple criterios (manifest válido + SW
 *     registrado + HTTPS). Si el usuario ya instaló o lo descartó hace
 *     poco, puede no llegar.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function useInstallPrompt() {
  const eventRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [available, setAvailable] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    function onBefore(e: Event) {
      // Prevenir el mini-bar que algunos browsers muestran solos.
      e.preventDefault();
      eventRef.current = e as BeforeInstallPromptEvent;
      setAvailable(true);
    }
    function onInstalled() {
      eventRef.current = null;
      setAvailable(false);
      setInstalled(true);
    }
    window.addEventListener('beforeinstallprompt', onBefore);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBefore);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  /**
   * Dispara el diálogo nativo "Instalar app". Solo funciona si
   * `available` es true (i.e. el browser ya emitió beforeinstallprompt).
   * Devuelve true si el usuario aceptó.
   */
  const promptInstall = useCallback(async (): Promise<boolean> => {
    const ev = eventRef.current;
    if (!ev) return false;
    try {
      await ev.prompt();
      const choice = await ev.userChoice;
      eventRef.current = null;
      setAvailable(false);
      return choice.outcome === 'accepted';
    } catch {
      return false;
    }
  }, []);

  return { available, installed, promptInstall };
}
